import { type ReactNode } from 'react';
import type { UseFormReturnType } from '@mantine/form';
import type { FormularDFormData } from '../../../types/reports.js';
import { useCalcValue, useValuesVersion, type CalcStore, type ValuesVersionStore } from '../formStores.js';
// Namensraum-Import statt benanntem Import der Umschalt-Konstante aus totals.ts: haelt ihre
// Auswertung auf genau eine Codezeile beschraenkt (siehe unten), damit ein Rueckbau (D-26/D-27)
// nachweisbar an einer einzigen Stelle haengt.
import * as totalsModule from './totals.js';

/**
 * Isolierte Anzeige-Summen-Blattkomponente (D-13, 18-CONTEXT.md-Vorgabe): abonniert den
 * valuesVersionStore selbst (useValuesVersion) statt aus geliftetem Parent-State zu lesen. Beim
 * Tippen rendert dadurch nur diese Zelle neu -- nie die umgebende Karte/Section/die Page. Kein
 * React.memo noetig, die Isolation passiert bereits durch das Abo (Muster aus NotesBadge/SubmitButton).
 */
export function DerivedTotal({
  form,
  store,
  compute,
  children,
}: {
  form: UseFormReturnType<FormularDFormData>;
  store: ValuesVersionStore;
  compute: (values: FormularDFormData) => string | null;
  children: (total: string | null) => ReactNode;
}) {
  useValuesVersion(store);
  const total = compute(form.getValues());
  return <>{children(total)}</>;
}

/**
 * Blattkomponente des Server-Zweigs: abonniert genau IHREN Schluessel aus dem Calc-Store
 * (D-05) ueber `useCalcValue`. Modulprivat, nicht exportiert -- der einzige Aufrufer ist
 * `FormularDTotal` unten.
 *
 * Der Hook MUSS in dieser eigenen Komponente stehen und nicht im Rumpf von `FormularDTotal`:
 * `FormularDTotal` hat ein fruehes `return` fuer den `USE_SERVER_CALC`-Umschalter, ein Hook
 * danach waere ein bedingter Hook (Laufzeit- und ESLint-Fehler); ein Hook davor liesse auch den
 * Client-Fallback (`DerivedTotal`) den Calc-Store abonnieren, den er nie liest.
 */
function ServerCalcTotal({
  store,
  serverKey,
  children,
}: {
  store: CalcStore;
  serverKey: string;
  children: (total: string | null) => ReactNode;
}) {
  const total = useCalcValue(store, serverKey);
  return <>{children(total ?? null)}</>;
}

/**
 * Der Umschalter aus D-26: entscheidet an genau dieser einen Stelle, ob eine Anzeige-Summe aus
 * dem Client (`DerivedTotal`) oder aus dem Server-Calc (`ServerCalcTotal`/`useCalcValue`) kommt.
 * Der Server-Zweig abonniert seit D-05 feldgenau ueber einen externen Store statt eine
 * `calc`-Prop zu lesen -- jede eintreffende Server-Antwort setzte sonst eine neue `calc`-
 * Referenz, die einen `memo()`-Comparator ueber dieser Komponente wirkungslos gemacht haette
 * (Plan 27-02). Faellt der Serveraufruf aus, fehlt der Schluessel im Store, `useCalcValue`
 * liefert `undefined`, die Komponente reicht `null` durch, und die Anzeige zeigt (ueber
 * `formatTonnage`) das '–' (D-28) -- derselbe Server-Zweig ist strikt lesend, es gibt keinen
 * Schreibpfad zurueck in `form` (T-23-05, dasselbe Muster wie
 * waterConsumption/FormularABCalculations).
 *
 * Alle Anzeigestellen in 23-03 und 23-06 nutzen ausschliesslich diese Komponente, damit der
 * Wechsel wirklich an einer Stelle haengt.
 */
export function FormularDTotal({
  form,
  store,
  calcStore,
  serverKey,
  compute,
  children,
}: {
  form: UseFormReturnType<FormularDFormData>;
  store: ValuesVersionStore;
  calcStore: CalcStore;
  serverKey: string;
  compute: (values: FormularDFormData) => string | null;
  children: (total: string | null) => ReactNode;
}) {
  if (totalsModule.USE_SERVER_CALC) {
    return (
      <ServerCalcTotal store={calcStore} serverKey={serverKey}>
        {children}
      </ServerCalcTotal>
    );
  }
  return (
    <DerivedTotal form={form} store={store} compute={compute}>
      {children}
    </DerivedTotal>
  );
}
